import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown } from 'antd';
import type { InventoryGroupField } from '../type';
import ArrowBack from '@/assets/images/ArrowBack';
import ChevronRight from '@/assets/images/ChevronRight';
import {
  ChevronDownIcon,
  CloseDrawerIcon,
  TableRows,
  TickIcon,
} from '@/assets/icons/icons';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';

type InventoryGroupingControlProps = {
  groupBy: InventoryGroupField;
  subGroupBy: InventoryGroupField;
  onChange: (value: {
    groupBy: InventoryGroupField;
    subGroupBy: InventoryGroupField;
  }) => void;
};

type GroupOptionValue = Exclude<InventoryGroupField, null>;
type ActiveView = 'root' | 'grouping' | 'subGrouping';

const GROUP_OPTIONS: ReadonlyArray<{
  label: string;
  value: GroupOptionValue;
}> = [
  { label: 'Location', value: 'location' },
  { label: `${themeT('team', theme.themeType)}`, value: 'team' },
  { label: 'Kit', value: 'kit' },
];

const prettify = (value: InventoryGroupField): string => {
  switch (value) {
    case 'location':
      return 'Location';
    case 'team':
      return `${themeT('team', theme.themeType)}`;
    case 'kit':
      return 'Kit';
    default:
      return '';
  }
};

const InventoryGroupingControl: React.FC<InventoryGroupingControlProps> = ({
  groupBy,
  subGroupBy,
  onChange,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [activeView, setActiveView] = useState<ActiveView>('root');

  const hasGrouping = Boolean(groupBy);

  const chipLabel = useMemo((): string => {
    if (groupBy && subGroupBy) {
      return `Group by ${prettify(groupBy)}, ${prettify(subGroupBy)}`;
    }

    if (groupBy) {
      return `Group by ${prettify(groupBy)}`;
    }

    return 'Group by';
  }, [groupBy, subGroupBy]);

  const handleOpenChange = useCallback((nextOpen: boolean): void => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setActiveView('root');
    }
  }, []);

  const resetGrouping = useCallback((): void => {
    onChange({
      groupBy: null,
      subGroupBy: null,
    });
  }, [onChange]);

  const handleClearSelection = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>): void => {
      event.stopPropagation();
      resetGrouping();
    },
    [resetGrouping]
  );

  const handleClearKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        resetGrouping();
      }
    },
    [resetGrouping]
  );

  const handleSelectGrouping = useCallback(
    (value: GroupOptionValue): void => {
      onChange({
        groupBy: value,
        subGroupBy: subGroupBy === value ? null : subGroupBy,
      });
      setOpen(false);
      setActiveView('root');
    },
    [onChange, subGroupBy]
  );

  const handleSelectSubGrouping = useCallback(
    (value: GroupOptionValue): void => {
      if (!groupBy || value === groupBy) return;

      onChange({
        groupBy,
        subGroupBy: value,
      });
      setOpen(false);
      setActiveView('root');
    },
    [groupBy, onChange]
  );

  const renderRootView = useCallback((): React.ReactNode => {
    return (
      <div className='inventory-group-menu'>
        <div className='inventory-group-menu-title'>Group by</div>

        <button
          type='button'
          className='inventory-group-menu-row'
          onClick={() => setActiveView('grouping')}
        >
          <span>Grouping</span>

          <span className='inventory-group-menu-row-right'>
            <span className='inventory-group-menu-selected-value'>
              {groupBy ? prettify(groupBy) : ''}
            </span>
            <ChevronRight />
          </span>
        </button>

        <button
          type='button'
          className='inventory-group-menu-row'
          onClick={() => {
            if (!groupBy) return;
            setActiveView('subGrouping');
          }}
          disabled={!groupBy}
        >
          <span>Sub-grouping</span>

          <span className='inventory-group-menu-row-right'>
            <span className='inventory-group-menu-selected-value'>
              {subGroupBy ? prettify(subGroupBy) : ''}
            </span>
            <ChevronRight />
          </span>
        </button>
      </div>
    );
  }, [groupBy, subGroupBy]);

  const renderSelectionView = useCallback(
    (title: string, type: 'grouping' | 'subGrouping'): React.ReactNode => {
      const selectedValue = type === 'grouping' ? groupBy : subGroupBy;

      return (
        <div className='inventory-group-menu'>
          <div className='inventory-group-back-row'>
            <ArrowBack
              fill='var(--text-secondary)'
              className='inventory-back-icon'
              onClick={() => {
                setActiveView('root');
              }}
            />
            <span className='inventory-group-back-text'>{title}</span>
          </div>

          <div className='inventory-group-options'>
            {GROUP_OPTIONS.map((item) => {
              const isDisabled =
                type === 'grouping'
                  ? subGroupBy === item.value
                  : !groupBy || groupBy === item.value;

              const isSelected = selectedValue === item.value;

              return (
                <button
                  key={item.value}
                  type='button'
                  className={`inventory-group-option ${
                    isSelected ? 'selected' : ''
                  }`}
                  disabled={isDisabled}
                  onClick={() => {
                    if (type === 'grouping') {
                      handleSelectGrouping(item.value);
                      return;
                    }

                    handleSelectSubGrouping(item.value);
                  }}
                >
                  <span>{item.label}</span>

                  {isSelected && (
                    <TickIcon
                      width={20}
                      height={20}
                      color='var(--text-primary)'
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );
    },
    [groupBy, subGroupBy, handleSelectGrouping, handleSelectSubGrouping]
  );

  const overlayContent = useMemo(() => {
    if (activeView === 'grouping') {
      return renderSelectionView('Grouping', 'grouping');
    }

    if (activeView === 'subGrouping') {
      return renderSelectionView('Sub-grouping', 'subGrouping');
    }

    return renderRootView();
  }, [activeView, renderRootView, renderSelectionView]);

  return (
    <Dropdown
      open={open}
      trigger={['click']}
      onOpenChange={handleOpenChange}
      overlayClassName='inventory-group-dropdown'
      dropdownRender={() => (
        <div
          className='inventory-group-dropdown-content'
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {overlayContent}
        </div>
      )}
    >
      <button
        type='button'
        className={`inventory-group-trigger ${hasGrouping ? 'selected' : ''}`}
        aria-label='Open grouping options'
      >
        <TableRows
          color={hasGrouping ? 'var(--surface-base)' : 'var(--text-primary)'}
          width={12}
          size={12}
        />

        {chipLabel}
        {!hasGrouping && <ChevronDownIcon size={16} />}
        {hasGrouping ? (
          <span
            className='inventory-group-trigger-close'
            onClick={handleClearSelection}
            onKeyDown={handleClearKeyDown}
            role='button'
            tabIndex={0}
            aria-label='Clear grouping selection'
          >
            <CloseDrawerIcon color='var(--surface-base)' size={13} />
          </span>
        ) : null}
      </button>
    </Dropdown>
  );
};

export default InventoryGroupingControl;
